/* eslint-disable camelcase, react/jsx-props-no-spreading */
import { Alert, Button, Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap";
import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { camelCase } from "lodash";
import classNames from "classnames";
import { useMutation } from "react-query";

import ACFCheckboxes from "../../../components/ACF/ACFCheckboxes";
import ACFInput from "../../../components/ACF/ACFInput";
import ACFOptInCheckbox from "../../../components/ACF/ACFOptInCheckbox";

import { decodeEntity } from "../../../helpers/authHelpers";
import formFields from "../../../acf-json/group_62990e6155800.json";
import { labelText } from "../../../components/ACF/acfHelpers";
import { updateUserMutation } from "../../../services/wp.user.service";
import { useAuth } from "../../../services/wp.auth.service";
import { useNotifications } from "../../../components/NotificationsContext";

const ProfileInfoForm = ({
	initialValues,
	onSuccess,
	submitText
}) => {
	const { user } = useAuth();
	const { addNotification } = useNotifications();

	const [formErrors, setFormErrors] = useState([]);
	const [formValues, setFormValues] = useState({
		profileCompleted: true,
		...user.attendeeDetails,
		...initialValues
	});

	const {
		isLoading,
		mutate
	} = updateUserMutation(useMutation, {
		onSuccess: ({ data }) => {
			if (data.errors) {
				setFormErrors(data.errors);
				addNotification({
					message: data.errors[0].message,
					type: "danger"
				});
			} else if (data?.data?.updateUser?.user) {
				if (onSuccess) {
					onSuccess();
				}
			}
		}
	});

	const handleChange = useCallback(({ target: { name, value } }) => {
		setFormValues((prevValue) => ({
			...prevValue,
			[name]: value
		}));
	}, []);

	const handleMultiChange = useCallback(({ target: { checked, name, value } }) => {
		if (checked && value) {
			setFormValues((prevValue) => ({
				...prevValue,
				// eslint-disable-next-line no-extra-parens
				[name]: [...(prevValue[name] || []), value]
			}));
		} else {
			setFormValues((prevValue) => ({
				...prevValue,
				[name]: (prevValue[name] || []).filter((chosen) => chosen !== value)
			}));
		}
	}, []);

	const handleOptInCheckboxChange = useCallback(({ target: { checked, name } }) => {
		setFormValues((prevValues) => ({
			...prevValues,
			[name]: Boolean(checked)
		}));
	}, []);

	const handleSubmit = useCallback((evt) => {
		evt.preventDefault();
		setFormErrors([]);
		mutate({
			attendeeDetails: {
				...formValues
			},
			id: user.databaseId
		});
	}, [formValues]);

	return (
		<Form id="profile-form" onSubmit={handleSubmit}>
			<Row>
				{formFields.fields.map(({
					allow_custom: allowOther,
					choices,
					default_value,
					instructions,
					label,
					key,
					message,
					name,
					required,
					type: inputType,
					wrapper
				}) => {
					const camelName = camelCase(name);

					if (wrapper.id === "terms-agreed") {
						return null;
					}

					return (
						<Col className={classNames("flex-fill", wrapper.class)} key={key} style={{ width: `${wrapper.width}%` }}>
							{inputType === "message"
								? <div className="acf-input acf-input-type-message" dangerouslySetInnerHTML={{ __html: message }} />
								: inputType === "checkbox"
									? (
										<ACFCheckboxes {
											...{
												allowOther: !!allowOther,
												choices,
												className: "mb-4",
												instructions,
												label,
												name: camelName,
												onChange: handleMultiChange,
												required: !!required,
												value: formValues[camelName]
											}}
										/>
									)
									: wrapper.id === "completed"
										? <Form.Control name={camelName} type="hidden" value={formValues[camelName]} />
										: inputType === "true_false"
											? (
												<ACFOptInCheckbox {
													...{
														default_value,
														instructions,
														label,
														message,
														name: camelName,
														onChange: handleOptInCheckboxChange,
														required,
														value: formValues[camelName]
													}}
												/>)
											: (
												<FloatingLabel
													className={classNames("mb-4")}
													controlId={camelName}
													label={labelText(label, required)}
													required={required}
												>
													{/* eslint-disable-next-line object-property-newline */}
													<ACFInput {
														...{
															choices,
															default_value,
															name: camelName,
															onChange: handleChange,
															placeholder: labelText(label, required),
															required: !!required,
															type: inputType || "text",
															value: formValues[camelName] || ""
														}}
													/>
												</FloatingLabel>)}
						</Col>
					);
				})}
			</Row>

			{!!formErrors.length && (
				<Alert variant="danger">
					{/* eslint-disable-next-line react/no-array-index-key */}
					{formErrors.map((error, idx) => <div dangerouslySetInnerHTML={{ __html: decodeEntity(error.message) }} key={idx} />)}
				</Alert>
			)}

			<div className="text-end">
				<Button disabled={isLoading} type="submit">
					{isLoading ? <Spinner animation="border" size="sm" /> : submitText}
				</Button>
			</div>
		</Form>
	);
};

ProfileInfoForm.propTypes = {
	initialValues: PropTypes.shape({}),
	onSuccess: PropTypes.func,
	submitText: PropTypes.string
};

ProfileInfoForm.defaultProps = {
	initialValues: {
		profileCompleted: true
	},
	onSuccess: () => null,
	submitText: "Save Changes"
};

export default ProfileInfoForm;
